/**
 * AdminNotificationBell
 *
 * A notification bell that lives in the admin sidebar footer.
 * Shows Super Admin platform broadcasts ONLY — completely separate from
 * school-owned announcements (which live in /admin/announcements).
 *
 * Features:
 *  - Unread badge count on the bell icon
 *  - Click to open a slide-out panel listing all broadcasts
 *  - Mark as Read (server-side read receipt + local state)
 *  - Dismiss (removes from panel, stored in localStorage)
 *  - Critical broadcasts optionally surface a temporary top-of-page banner
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, X, Check, Megaphone } from 'lucide-react';

import { SERVER_BASE } from '../api/client';
const API = SERVER_BASE + '/api/superadmin';
const LS_DISMISSED = 'pn_dismissed_v1'; // platform notifications dismissed

const TYPE_CFG = {
  info:        { icon: 'ℹ️',  color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE', label: 'Info'        },
  warning:     { icon: '⚠️',  color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', label: 'Warning'     },
  maintenance: { icon: '🔧',  color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE', label: 'Maintenance' },
  feature:     { icon: '✨',  color: '#059669', bg: '#ECFDF5', border: '#A7F3D0', label: 'New Feature' },
  urgent:      { icon: '🚨',  color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', label: 'Urgent'      },
  critical:    { icon: '🔴',  color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', label: 'Critical'    },
};

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function readDismissed() {
  try { return JSON.parse(localStorage.getItem(LS_DISMISSED) || '[]').map(String); } catch { return []; }
}
function saveDismissed(arr) {
  try { localStorage.setItem(LS_DISMISSED, JSON.stringify(arr)); } catch {}
}

export default function AdminNotificationBell({ schoolId, sidebarColor = '#1a3a5c' }) {
  const [broadcasts, setBroadcasts]   = useState([]);
  const [dismissed,  setDismissed]    = useState(readDismissed);
  const [open,       setOpen]         = useState(false);
  const [criticalBanner, setCriticalBanner] = useState(null); // single critical to surface
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  /* ── Fetch broadcasts for this school ── */
  const fetch_ = useCallback(() => {
    if (!schoolId) return;
    fetch(`${API}/broadcasts/for-school/${schoolId}?audience=admins`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        setBroadcasts(data);

        // Surface the first unseen critical broadcast as a banner
        const dismissed_ = readDismissed();
        const unreadCritical = data.find(
          b => b.type === 'critical' && !b.is_read && !dismissed_.includes(String(b._id))
        );
        setCriticalBanner(prev => {
          // Only set if not already showing one (avoid flicker on poll)
          if (prev) return prev;
          return unreadCritical || null;
        });
      })
      .catch(() => {});
  }, [schoolId]);

  useEffect(() => {
    fetch_();
    const id = setInterval(fetch_, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetch_]);

  /* ── Close panel on outside click ── */
  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (
        panelRef.current  && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  /* ── Mark as read (server + local state) ── */
  const markRead = useCallback((id) => {
    if (!schoolId) return;
    fetch(`${API}/broadcasts/${id}/read`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ school_id: schoolId }),
    }).catch(() => {});
    setBroadcasts(prev => prev.map(b => String(b._id) === String(id) ? { ...b, is_read: true } : b));
    if (criticalBanner && String(criticalBanner._id) === String(id)) setCriticalBanner(null);
  }, [schoolId, criticalBanner]);

  /* ── Dismiss (hides from panel permanently, also marks read) ── */
  const dismiss = useCallback((id) => {
    markRead(id);
    const next = [...readDismissed().filter(x => x !== String(id)), String(id)];
    saveDismissed(next);
    setDismissed(next);
    if (criticalBanner && String(criticalBanner._id) === String(id)) setCriticalBanner(null);
  }, [markRead, criticalBanner]);

  /* ── Derived state ── */
  const visible    = broadcasts.filter(b => !dismissed.includes(String(b._id)));
  const unreadCount = visible.filter(b => !b.is_read).length;

  /* ── Mark all visible as read when panel opens ── */
  const handleOpen = () => {
    setOpen(o => !o);
  };

  /* ── Mark all as read ── */
  const markAllRead = () => {
    visible.filter(b => !b.is_read).forEach(b => markRead(b._id));
  };

  return (
    <>
      {/* ── Critical broadcast banner (fixed below impersonation bar / at top) ── */}
      {criticalBanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 8500,
          background: '#7f1d1d',
          borderBottom: '2px solid #ef4444',
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: '#fff',
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>🔴</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 800, fontSize: 13, marginRight: 8 }}>{criticalBanner.title}</span>
            <span style={{ fontSize: 12, opacity: 0.85 }}>{criticalBanner.message}</span>
          </div>
          <button
            onClick={() => markRead(criticalBanner._id)}
            style={{ padding: '5px 14px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 7, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
          >
            Got it
          </button>
          <button
            onClick={() => dismiss(criticalBanner._id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: 4, flexShrink: 0 }}
            title="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── Bell button (inside sidebar) ── */}
      <div style={{ position: 'relative' }}>
        <button
          ref={buttonRef}
          onClick={handleOpen}
          title="Platform Notifications"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '9px 12px',
            background: open ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 9,
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.75)',
            fontSize: 13,
            fontWeight: 600,
            textAlign: 'left',
            position: 'relative',
          }}
        >
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Bell size={16} />
            {unreadCount > 0 && (
              <div style={{
                position: 'absolute',
                top: -5, right: -6,
                minWidth: 15, height: 15,
                borderRadius: 8,
                background: '#EF4444',
                color: '#fff',
                fontSize: 9, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 3px',
                border: `2px solid ${sidebarColor}`,
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </div>
            )}
          </div>
          <span>Notifications</span>
          {unreadCount > 0 && (
            <span style={{
              marginLeft: 'auto',
              background: '#EF4444',
              color: '#fff',
              fontSize: 10, fontWeight: 800,
              borderRadius: 10, padding: '1px 6px',
            }}>
              {unreadCount}
            </span>
          )}
        </button>

        {/* ── Slide-out notification panel ── */}
        {open && (
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: 224,    // sidebar width
              bottom: 80,
              width: 360,
              maxHeight: 520,
              background: '#fff',
              border: '1.5px solid #E5E7EB',
              borderRadius: 16,
              boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
              zIndex: 8000,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Panel header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px 12px',
              borderBottom: '1px solid #F3F4F6',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Megaphone size={15} color="#6366F1" />
                <span style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>Platform Notifications</span>
                {unreadCount > 0 && (
                  <span style={{ background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 8, padding: '1px 6px' }}>
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#6366F1', fontWeight: 600, padding: '3px 8px', borderRadius: 6 }}
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 }}
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Notification list */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {visible.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9CA3AF' }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🔔</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280' }}>No notifications</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Platform updates from Super Admin will appear here.</div>
                </div>
              ) : visible.map((b, idx) => {
                const cfg = TYPE_CFG[b.type] || TYPE_CFG.info;
                const isUnread = !b.is_read;
                return (
                  <div
                    key={b._id}
                    style={{
                      padding: '12px 16px',
                      borderBottom: idx < visible.length - 1 ? '1px solid #F9FAFB' : 'none',
                      background: isUnread ? cfg.bg : '#fff',
                      borderLeft: `3px solid ${isUnread ? cfg.color : 'transparent'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      {/* Icon */}
                      <div style={{
                        width: 34, height: 34,
                        borderRadius: 9,
                        background: `${cfg.color}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, flexShrink: 0,
                      }}>
                        {cfg.icon}
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: isUnread ? 800 : 600, color: '#111827' }}>{b.title}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700,
                            color: cfg.color, background: `${cfg.color}18`,
                            borderRadius: 4, padding: '1px 6px',
                          }}>{cfg.label}</span>
                          {isUnread && (
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color, display: 'inline-block', flexShrink: 0 }} />
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.5, marginBottom: 4 }}>{b.message}</div>
                        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                          {fmtDate(b.createdAt)}
                          {b.expires_at && ` · Expires ${fmtDate(b.expires_at)}`}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
                      {isUnread && (
                        <button
                          onClick={() => markRead(b._id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11, color: '#374151', cursor: 'pointer', fontWeight: 600 }}
                        >
                          <Check size={11} /> Mark read
                        </button>
                      )}
                      <button
                        onClick={() => dismiss(b._id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11, color: '#9CA3AF', cursor: 'pointer' }}
                      >
                        <X size={11} /> Dismiss
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div style={{
              padding: '8px 16px',
              borderTop: '1px solid #F3F4F6',
              fontSize: 11,
              color: '#9CA3AF',
              textAlign: 'center',
              flexShrink: 0,
            }}>
              From Super Admin · School announcements are in{' '}
              <span style={{ color: '#6366F1', fontWeight: 600 }}>Announcements</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
